import { IProcessSettings } from './../../../shared/interfaces/process-settings';
import { Observable, Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import { map, tap, filter, distinctUntilChanged } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';

import Response = ZoningAPI.Response;
import IZoningCodeVersion = ZoningAPI.IZoningCodeVersion;

import { BaseEntityService } from '../../../shared/services/base-entity.service';
import { TaskService } from './task.service';
import { InstanceService } from './instance.service';
import { ZoningCodeVersion, ZoningCodeVersionFactory } from '../models/zoning-code-version';
import { ZoningCode } from '../models/zoning-code';
import { Instance } from '../models/instance';
import * as _ from 'lodash';
import { PropertyRecordRelationType } from 'src/app/shared/enums/property-record-relation-type';

const API_ENDPOINT = environment.ZONAR_API_URL + '/zoning-code';

@Injectable({
    providedIn: 'root'
})
export class ZoningCodeVersionService extends BaseEntityService<ZoningCodeVersion> {
    private readonly zoningCodesVersionsSubject = new BehaviorSubject<ZoningCodeVersion[]>([]);

    readonly zoningCodesVersions = this.zoningCodesVersionsSubject.asObservable().pipe(distinctUntilChanged());

    newRecalculateZCVersionProcess = new Subject<string>();

    constructor(
        private readonly http: HttpClient,
        private readonly taskService: TaskService,
        private readonly snackBar: MatSnackBar,
        private readonly instanceService: InstanceService
    ) {
        super(ZoningCodeVersionFactory);

        // Bind tasks to zoning code versions
        combineLatest(this.zoningCodesVersions, this.taskService.processes).subscribe(([versions, tasks]) => {
            versions.forEach(version => version.updateTasksFromList(tasks));
            this.instanceService.updateSelectedInstanceStatistic();
        });
    }

    getVersionsForZoningCode(zoningCode: ZoningCode, stateEnv?: string) {
        if (!zoningCode) {
            return of([]);
        }

        const fromObject: any = {};

        if (stateEnv) {
            fromObject.state_env = stateEnv;
        }

        const params = new HttpParams({ fromObject });

        return this.http
            .get<Response>(`${API_ENDPOINT}/${zoningCode.Id}/version`, { params })
            .pipe(
                map(response => this.initEntities(response.data)),
                tap(zcVersions => this.zoningCodesVersionsSubject.next(zcVersions))
            );
    }

    getAttachmentLinksForZCVersion(zcVersionId: number) {
        if (!Number.isInteger(zcVersionId)) {
            return;
        }

        return this.http.get<Response>(`${environment.ZONAR_API_URL}/zoning-code-version/${zcVersionId}/attachments`);
    }

    getFailedFoliosAttachment(zcVersionId: number) {
        if (!Number.isInteger(zcVersionId)) {
            return;
        }

        return this.http.post<Response>(
            `${environment.ZONAR_API_URL}/zoning-code-version/${zcVersionId}/attachments/failed-properties`,
            {}
        );
    }

    getPropertiesWithNotificationsAttachment(zcVersionId: number) {
        if (!Number.isInteger(zcVersionId)) {
            return;
        }

        return this.http.post<Response>(
            `${environment.ZONAR_API_URL}/zoning-code-version/${zcVersionId}/attachments/properties-with-notifications`,
            {}
        );
    }

    generateMissedEntitiesAttachment(zcVersionId: number, relationType: PropertyRecordRelationType) {
        if (!Number.isInteger(zcVersionId) || !Number.isInteger(relationType)) {
            return of(null);
        }

        return this.http.post<Response>(
            `${environment.ZONAR_API_URL}/zoning-code-version/${zcVersionId}/attachments/missed-entities?relationType=${relationType}`,
            {}
        );
    }

    checkZoningCodeVersion(zoningCode: ZoningCode) {
        if (!zoningCode) {
            return;
        }

        return this.http.get<Response>(`${API_ENDPOINT}/${zoningCode.Id}/check-version`).pipe(
            tap(response => {
                if (response.data === null) {
                    this.snackBar.open(`Last version was added`, 'OK', {
                        verticalPosition: 'top',
                        horizontalPosition: 'center',
                        duration: 2000
                    });
                }
            }),
            filter(response => response.data !== null),
            map(response => this.initEntity(response.data))
        );
    }

    recalculateZCVersion(
        instance: Instance,
        zcVersion: ZoningCodeVersion,
        zoningCode: ZoningCode,
        needToDeleteCurrentCalculation?: boolean,
        processSettings?: IProcessSettings
    ) {
        if (!instance || !zcVersion) {
            return;
        }
        const currentCalculationTask = zcVersion && zcVersion.Tasks.length && zcVersion.Tasks[0];
        const removeOperation =
            needToDeleteCurrentCalculation && currentCalculationTask
                ? this.taskService.deleteTask(currentCalculationTask)
                : of(null);

        removeOperation.subscribe(() => {
            this.taskService
                .recalculateZCVersion(instance, zcVersion.Id, zoningCode.GridicsId, processSettings)
                .subscribe(process => {
                    this.newRecalculateZCVersionProcess.next(process.id);
                });
        });
    }

    deleteZCVersionCalculationResults(zcVersionId: number) {
        if (!zcVersionId) {
            return;
        }

        return this.http.delete<Response>(`${environment.ZONAR_API_URL}/zoning-code-version/${zcVersionId}/results`);
    }

    removeZoningCode(zoningCodeId: number) {
        if (!zoningCodeId) {
            return;
        }

        return this.http.delete<Response>(`${API_ENDPOINT}/${zoningCodeId}`);
    }

    updateZCVStatus(zoningCodeVersion: ZoningCodeVersion, stateEnv: string): Observable<IZoningCodeVersion> {
        if (!_.get(zoningCodeVersion, 'Id')) {
            return of(null);
        }

        const fromObject: any = {};
        if (stateEnv) {
            fromObject.state_env = stateEnv;
        }
        const params = new HttpParams({ fromObject });

        return this.http
            .post<Response>(
                `${environment.ZONAR_API_URL}/zoning-code-version/${zoningCodeVersion.Id}/sync`,
                {},
                { params }
            )
            .pipe(map(response => response.data));
    }
}
